import {
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  Td,
  Tr,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import { ExpiryReason } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { aircraftTypeOptions, expiryReasonOptions, MAX_PARAMS } from '@pelicargo/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { HiArchiveBoxArrowDown, HiEllipsisVertical, HiTrash } from 'react-icons/hi2';

import { trpc } from '../config/trpc';
import { AircraftInput } from './Autocomplete/AircraftInput';
import { SimpleInput } from './SimpleInput';
import { SimpleLabeledPopoverInput } from './SimpleLabeledPopoverInput';
import { SimpleNumber } from './SimpleNumber';
import { SimpleSelect } from './SimpleSelect';
import { SimpleWeekdaySelect } from './SimpleWeekdaySelect';

type Props = {
  name: string;
  isDeleteDisabled?: boolean;
  removeFromForm: () => void;
};

export const SegmentEditorItemFormRow = ({ name, isDeleteDisabled = false, removeFromForm }: Props) => {
  const { control, setValue, watch } = useFormContext();

  const toast = useToast();

  const isSchedule = useWatch({ control, name: `${name}.is_schedule` });
  const id = useWatch({ control, name: `${name}.id` });
  const providerId = useWatch({ control, name: `${name}.provider_id` });
  const aircraftId = useWatch({ control, name: `${name}.aircraft_id` });

  const { onCopy } = useClipboard(id);
  const isCreated = useMemo(() => id > 0, [id]);

  const { data: aircraftConstraint } = trpc.getAircraftConstraint.useQuery(
    { aircraft_id: aircraftId, provider_id: providerId },
    { enabled: !!aircraftId && !!providerId },
  );

  useEffect(() => {
    if (aircraftConstraint) {
      setValue(`${name}.aircraft_type`, aircraftConstraint?.aircraft_type, { shouldDirty: true });
      setValue(`${name}.max_cm_length`, aircraftConstraint?.max_length || 1, { shouldDirty: true });
      setValue(`${name}.max_cm_width`, aircraftConstraint?.max_width || 1, { shouldDirty: true });
      setValue(`${name}.max_cm_height`, aircraftConstraint?.max_height || 1, { shouldDirty: true });
      setValue(`${name}.max_kg_per_piece`, aircraftConstraint?.max_weight || 1, { shouldDirty: true });
    }
  }, [aircraftConstraint, name, setValue]);

  useEffect(() => {
    // Subscribe to form changes without re-renders
    // This adjusts the max dimensions when the aircraft type changes
    const subscription = watch((values, { name }) => {
      // Skip if the changes are not for legs[].segments[].aircraft_type
      if (!name?.match(/legs\.\d+\.segments\.\d+\.aircraft_type/)) return;

      const nameParts = name.split('.');
      const nextName = nameParts.slice(0, -1).join('.');

      // Find the default values for the aircraft type
      const aircraftType = nameParts.reduce((obj, key) => obj?.[key], values);
      const defaults = MAX_PARAMS?.[aircraftType];

      setValue(`${nextName}.max_cm_length`, defaults?.length || 1, { shouldDirty: true });
      setValue(`${nextName}.max_cm_width`, defaults?.width || 1, { shouldDirty: true });
      setValue(`${nextName}.max_cm_height`, defaults?.height || 1, { shouldDirty: true });
      setValue(`${nextName}.max_kg_per_piece`, defaults?.weight_per_piece || 1, { shouldDirty: true });
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const handleMarkSegment = useCallback(
    (reason: ExpiryReason) => () => {
      // Don't use this if it's not created in the DB
      if (!isCreated) return;

      setValue(`${name}.expiry_reason`, reason, { shouldDirty: true });
      setValue(`${name}.expires_at`, new Date(), { shouldDirty: true });
    },
    [isCreated, setValue, name],
  );

  return (
    <Tr key={name} whiteSpace="nowrap">
      <Td p="0" pl={2}>
        <SimpleInput name={`${name}.provider_id`} hidden />
        <SimpleInput name={`${name}.id`} hidden />
        <Menu>
          <MenuButton as={IconButton} icon={<HiEllipsisVertical />} variant="ghost" aria-label="More Options" />
          <Portal>
            <MenuList zIndex="modal">
              <MenuGroup title="Delete Options">
                <Show if={!isCreated}>
                  <MenuItem icon={<HiTrash />} isDisabled={isDeleteDisabled} onClick={removeFromForm}>
                    Remove
                  </MenuItem>
                </Show>
                <Show if={isCreated}>
                  <>
                    <MenuItem
                      icon={<HiArchiveBoxArrowDown />}
                      isDisabled={isDeleteDisabled}
                      onClick={handleMarkSegment(ExpiryReason.ROUTE_ENDED)}
                    >
                      Mark Route-Ended
                    </MenuItem>
                    <MenuItem
                      icon={<HiTrash />}
                      isDisabled={isDeleteDisabled}
                      onClick={handleMarkSegment(ExpiryReason.INVALID_ENTRY)}
                    >
                      Mark Invalid Entry
                    </MenuItem>
                  </>
                </Show>
              </MenuGroup>
              <MenuDivider />
              <MenuGroup title="Details">
                <MenuItem
                  command="Flight Segment ID"
                  isDisabled={!id}
                  onClick={() => {
                    if (!id) return;
                    onCopy(id);
                    toast({ description: 'Copied to clipboard' });
                  }}
                >
                  {id ?? 'N/A'}
                </MenuItem>
              </MenuGroup>
            </MenuList>
          </Portal>
        </Menu>
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <AircraftInput valueKey="id" name={`${name}.aircraft_id`} size="md" borderRadius={0} />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleSelect
          name={`${name}.aircraft_type`}
          options={aircraftTypeOptions}
          size="sm"
          variant="filled"
          borderRadius={0}
          selectProps={{
            borderRadius: 0,
            borderWidth: 0,
            paddingInline: 0,
            icon: <></>,
            bg: 'none',
          }}
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleNumber
          size="sm"
          precision={2}
          inputAttrs={{
            borderRadius: 0,
            borderWidth: 0,
            paddingRight: 1,
            textAlign: 'left',
            bg: 'none',
            _hover: { bg: 'none' },
          }}
          variant="unstyled"
          name={`${name}.max_cm_length`}
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleNumber
          size="sm"
          precision={2}
          inputAttrs={{
            borderRadius: 0,
            borderWidth: 0,
            paddingRight: 1,
            textAlign: 'left',
            bg: 'none',
            _hover: {
              bg: 'none',
            },
          }}
          variant="unstyled"
          name={`${name}.max_cm_width`}
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleNumber
          size="sm"
          precision={2}
          inputAttrs={{
            borderRadius: 0,
            borderWidth: 0,
            paddingRight: 1,
            textAlign: 'left',
            bg: 'none',
            _hover: {
              bg: 'none',
            },
          }}
          variant="unstyled"
          name={`${name}.max_cm_height`}
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleNumber
          size="sm"
          precision={2}
          inputAttrs={{
            borderRadius: 0,
            borderWidth: 0,
            paddingRight: 1,
            textAlign: 'left',
            bg: 'none',
            _hover: {
              bg: 'none',
            },
          }}
          variant="unstyled"
          name={`${name}.max_kg_per_piece`}
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleLabeledPopoverInput
          inputType="date-picker"
          name={`${name}.starts_at`}
          isDisabled={!isSchedule}
          showClearButton
          border={0}
          p={0}
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleLabeledPopoverInput
          inputType="date-picker"
          name={`${name}.expires_at`}
          isDisabled={!isSchedule}
          showClearButton
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleLabeledPopoverInput
          inputType="date-picker"
          name={`${name}.schedule_departs_at`}
          isDisabled={!isSchedule}
          showClearButton
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleLabeledPopoverInput
          inputType="date-picker"
          name={`${name}.schedule_arrives_at`}
          isDisabled={!isSchedule}
          showClearButton
        />
      </Td>
      <Td minW="75px" p="0" borderRightWidth={1}>
        <SimpleNumber
          precision={2}
          inputAttrs={{
            borderRadius: 0,
            borderWidth: 0,
            paddingRight: 1,
            textAlign: 'left',
            bg: 'none',
            _hover: {
              bg: 'none',
            },
          }}
          variant="unstyled"
          width="5em"
          name={`${name}.flight_number`}
          isDisabled={!isSchedule}
        />
      </Td>
      <Td minW="75px" px="2" borderRightWidth={1}>
        <SimpleWeekdaySelect name={`${name}.schedule`} isDisabled={!isSchedule} />
      </Td>
      <Td minW="75px" p="0">
        <SimpleSelect
          name={`${name}.expiry_reason`}
          options={expiryReasonOptions}
          size="md"
          variant="filled"
          borderRadius={0}
          selectProps={{
            borderRadius: 0,
            borderWidth: 0,
            paddingInline: 0,
            icon: <></>,
            bg: 'none',
          }}
        />
      </Td>
    </Tr>
  );
};
